import { css, keyframes, styled } from "styled-components";

import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import { theme } from "@ubnt/store.core/lib/constants/theme";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";

const scrollBarHeight = 16;
const PRODUCT_CLICKABLE_AREA_Z_INDEX = 1;
export const PRODUCT_CARD_WIDTH = 205;
export const PRODUCT_LIST_GRID_GAP = 10;

const productCardAnimation = keyframes`
50% {
  transform: scale(1.025);
}
    100% {
        box-shadow: 0 0 13px -1px rgb(33 35 39 / 0.174);
        /* we can't return to scale(1) because this creates a bug where the contents of the product card slightly shift on hover */
        transform: scale(0.999);
    }
`;

const productGroupLineAnimation = keyframes`
    25% {
        background: ${theme.colors.primaryBlue};
        transform: scaleY(1);
    }

    50% {
      background: ${theme.colors.primaryBlue};
      transform: scaleY(1.5);
    }

    70% {
      background: ${theme.colors.primaryBlue};
      transform: scaleY(1.5);
      width: 100%;
    }

    100% {
      background: rgba(0, 111, 255, 0.00);
      transform: scaleY(1);
      width: 100%;
    }
`;

// seconds
const productGroupAnimationDuration = 0.125;

// seconds
const productCardAnimationDuration = 0.25;

export const Wrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

export const Container = styled.div<{
  $isHovering: boolean;
  $moreThanOneGroupExists: boolean;
  $isDragging: boolean;
}>`
  overflow-x: auto;
  padding-bottom: 30px;
  scroll-behavior: smooth;
  scroll-behavior: ${({ $isDragging }) => ($isDragging ? "unset" : "smooth")};

  ${({ $moreThanOneGroupExists }) =>
    !$moreThanOneGroupExists &&
    css`
      padding-top: 7px;
    `}

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    /* We need the x-padding so the shadows of the cards aren't clipped */
    padding: 0 5px 24px;
  }

  /**
      * We cannot define padding on some of these scroll bar
      * elements. Because of this, we're defining scroll bar colors
      * with linear-gradient so users don't have to be very
      * accurate when clicking the scroll bar while keeping the
      * styles of the scroll bar slim.
   */

  &::-webkit-scrollbar {
    height: ${scrollBarHeight}px;
    /* at the top of this componnent's definition is a note on why we're defining colors this way  */
    background-image: linear-gradient(
      to bottom,
      transparent 6px,
      #eeeff1 6px,
      #eeeff1 8px,
      transparent 8px
    );
  }

  &::-webkit-scrollbar-thumb {
    /* at the top of this componnent's definition is a note on why we're defining colors this way  */
    background-image: linear-gradient(
      to bottom,
      transparent 6px,
      ${theme.colors.primaryBlue} 6px,
      ${theme.colors.primaryBlue} 8px,
      transparent 8px
    );

    ${({ $isHovering }) =>
      $isHovering &&
      css`
        /* at the top of this componnent's definition is a note on why we're defining colors this way  */
        background-image: linear-gradient(
          to bottom,
          transparent 5px,
          ${theme.colors.primaryBlue} 5px,
          ${theme.colors.primaryBlue} 9px,
          transparent 9px
        );
      `}
  }
`;

export const ProductGroupProductListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`;

export const ProductGroups = styled.div<{ $columns: number }>`
  min-width: 100%;
  display: grid;
  gap: 0 10px;
  grid-template-columns: repeat(
    ${({ $columns }) => $columns},
    ${PRODUCT_CARD_WIDTH}px
  );
  padding: 0 0 10px 10px;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    padding-left: 0;
  }
`;

export const ProductGroupLabelContainer = styled.div<{ $span: number }>`
  display: flex;
  grid-column: span ${({ $span }) => $span ?? 1};
  gap: 0 20px;
`;

export const ProductGroupLabel = styled.span<{
  $isActive: boolean;
  $productGroupsSelectorExists: boolean;
}>`
  color: #808893;
  font-size: 18px;
  font-weight: 700;
  scroll-margin-left: 10px;
  white-space: nowrap;

  ${({ $isActive, $productGroupsSelectorExists }) =>
    ($isActive || !$productGroupsSelectorExists) &&
    css`
      color: ${theme.colors.primaryBlue};
    `}
`;

export const ProductGroupLabelLineContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const ProductGroupLabelGrayLine = styled.div`
  width: 100%;
  height: 1px;
  background: #eeeff1;
`;

export const ProductGroupLabelLine = styled.div<{
  $isActive: boolean;
  $playAnimation: boolean;
  $productGroupsSelectorExists: boolean;
}>`
  width: 0;
  height: 1px;
  background: #eeeff1;

  ${({ $isActive, $playAnimation, $productGroupsSelectorExists }) =>
    !$productGroupsSelectorExists
      ? css`
          animation: ${productGroupLineAnimation} ease-in-out 1 forwards;
        `
      : $isActive &&
        $playAnimation &&
        css`
          animation: ${productGroupLineAnimation}
            ${productGroupAnimationDuration + productCardAnimationDuration}s
            ease-in-out 1 forwards;
        `}
`;

export const ProductList = styled.div<{
  $columns: number;
  $isDragging: boolean;
  $overflowExists: boolean;
  $moreThanOneGroupExists: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $columns }) => $columns},
    ${PRODUCT_CARD_WIDTH}px
  );
  align-items: flex-end;
  gap: 0 ${PRODUCT_LIST_GRID_GAP}px;
  padding: 0 10px;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    padding-left: 6px;
  }

  ${({ $overflowExists }) =>
    $overflowExists &&
    css`
      cursor: grab;
    `}

  ${({ $isDragging, $overflowExists }) =>
    $overflowExists &&
    $isDragging &&
    css`
      cursor: grabbing;
    `}

    ${({ $moreThanOneGroupExists }) =>
    !$moreThanOneGroupExists &&
    css`
      margin-top: 5px;
    `}
`;

export const ProductCardOnClickArea = styled.div`
  position: absolute;
  z-index: ${PRODUCT_CLICKABLE_AREA_Z_INDEX};
  width: 100%;
  height: 100%;
`;

export const Product = styled.div<{
  $isActive: boolean;
  $groupIndex: number;
  $playAnimation: boolean;
  $groupLength: number;
  $canPlayAniimation: boolean;
  $productGroupsSelectorExists: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background: ${theme.colors.white};
  padding: 20px 10px;
  border-radius: 6px;
  height: 100%;
  min-height: 100%;
  -webkit-user-drag: none;
  user-select: none;
  /* can't use scale(1) because the content slightly shifts on hover */
  transform: scale(0.999);

  ${({
    $isActive,
    $groupIndex,
    $playAnimation,
    $groupLength,
    $canPlayAniimation,
    $productGroupsSelectorExists,
  }) =>
    !$productGroupsSelectorExists
      ? css`
          animation: ${productCardAnimation} ease-in-out 1 forwards;
        `
      : $canPlayAniimation &&
        $isActive &&
        $playAnimation &&
        css`
          animation: ${productCardAnimation} ${productCardAnimationDuration}s
            ease-in-out
            ${(productGroupAnimationDuration / $groupLength) * $groupIndex}s 1
            forwards;
        `}
`;

export const Image = styled.img`
  -webkit-user-drag: none;
  user-select: none;
`;

export const DescriptionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  user-select: none;

  justify-content: space-between;
  flex-grow: 1;
`;

export const Name = styled.div`
  color: #212427;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.32px;
  line-height: normal;

  display: flex;
  justify-content: center;
`;

export const Description = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.28px;
  color: rgb(0 0 0 / 0.65);
  display: flex;
  justify-content: center;
  margin-top: 6px;
`;

export const ButtonIcon = styled(StyleableSvgImage)<{ $isSelected: boolean }>`
  transition: transform 0.125s ease-in-out;
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      transform: rotate(45deg);
    `}
`;

export const Button = styled.button<{
  $isStaticProduct: boolean;
  $isSelected: boolean;
  $disabled: boolean;
}>`
  align-items: center;
  background: transparent;
  border-radius: 4px;
  border: 1px solid #808893;
  color: #808893;
  cursor: pointer;
  display: flex;
  gap: 4px;
  margin: 12px auto 0;
  min-width: 110px;
  padding: 10px 15px;
  transition: 0.3s;
  z-index: ${PRODUCT_CLICKABLE_AREA_Z_INDEX};

  &:hover {
    background: #80889310;
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      background: ${theme.colors.primaryBlue};
      border: 1px solid ${theme.colors.primaryBlue};
      color: ${theme.colors.white};

      &:hover {
        background: ${theme.colors.dodgerblue};
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed !important;

      @media ${BREAKPOINTS.TABLET_AND_DOWN} {
        border: 1px solid rgb(73 73 73 / 0.6);
        background: rgb(73 73 73 / 0.6);
        color: ${theme.colors.white};

        &:hover {
          background: rgb(73 73 73 / 0.6);
        }
      }
    `}

    ${({ $isStaticProduct }) =>
    $isStaticProduct &&
    css`
      visibility: hidden;
      pointer-events: none;
    `}
`;

export const CopyWhiteSpace = styled.span`
  display: inline-block;
  width: 5px;
`;

export const ButtonCopy = styled.span`
  font-size: 14px;
  font-weight: 700;
`;
